/**
 * Find a book cover we can STAND BEHIND, or return null.
 *
 * WHY. Covers were chosen by taking the first search hit and trusting it:
 *   - The Unteleported Man showed "Mensonges & Cie", the French Robert Laffont
 *     edition, because Open Library's title search returns a WORK and its
 *     cover_i can belong to any edition in any language.
 *   - Neuromancer showed a blurry scanned library title page, because Google
 *     Books' first result was a digitised library copy whose "cover" is page 1.
 * Nothing checked the image afterwards, so each one had to be spotted by eye.
 *
 * THE RULE. A cover is accepted only when:
 *   1. it comes from an edition we can tie to THIS book -- the exact ISBN, or an
 *      English edition whose title and author both match; and
 *   2. the image itself passes checkCoverImage: it loads, is book-shaped, is big
 *      enough to enlarge (>= MIN_HEIGHT px tall), and is not a known
 *      "no cover" placeholder.
 * Otherwise null: the site shows its designed placeholder, never a guess.
 *
 * Sources, in order: Open Library by an English-area ISBN (exact edition) ->
 * Apple Books UK store (publisher artwork at 1200px, exact title + author only)
 * -> Open Library editions of the matching work that are English by BOTH their
 * language tag and their ISBN. Google Books is deliberately NOT a source: its
 * scanned library copies are where title-page "covers" come from.
 *
 * The placeholder sizes and shape limits mirror src/utils/coverPlaceholder.ts;
 * src/utils/__tests__/coverRulesInSync.test.ts fails if they drift apart.
 */

// Google Books' "image not available" graphic at every size it has been seen.
export const NO_COVER_DIMENSIONS: ReadonlyArray<readonly [number, number]> = [
  [128, 170],
  [575, 750],
  [300, 391],
];
/** Height / width band for a real book cover (observed 1.29-1.69). */
export const MIN_RATIO = 1.2;
export const MAX_RATIO = 1.75;
/** Below this the fixed-size enlarge view visibly upscales. */
export const MIN_HEIGHT = 400;

export interface CoverCheck {
  ok: boolean;
  reason?: string;
  width?: number;
  height?: number;
}

export interface VerifiedCover {
  url: string;
  source: 'openlibrary-isbn' | 'openlibrary-edition' | 'apple';
  width: number;
  height: number;
}

const UA = { 'User-Agent': 'Leafnode/1.0 (connect@leafnode.co.uk)' };

async function fetchWithTimeout(url: string, ms = 8000): Promise<Response | null> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), ms);
  try {
    return await fetch(url, { headers: UA, signal: ctl.signal, redirect: 'follow' });
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

/** Width/height from the first bytes of a JPEG, PNG or WebP. */
export function imageSize(b: Uint8Array): { width: number; height: number } | null {
  // PNG: IHDR at byte 16
  if (b.length > 24 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
    const dv = new DataView(b.buffer, b.byteOffset);
    return { width: dv.getUint32(16), height: dv.getUint32(20) };
  }
  // JPEG: walk segments to the first SOFn
  if (b.length > 4 && b[0] === 0xff && b[1] === 0xd8) {
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) { i++; continue; }
      const marker = b[i + 1];
      const len = (b[i + 2] << 8) | b[i + 3];
      const isSOF = marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker);
      if (isSOF) return { height: (b[i + 5] << 8) | b[i + 6], width: (b[i + 7] << 8) | b[i + 8] };
      i += 2 + len;
    }
    return null;
  }
  // WebP (RIFF....WEBP)
  if (b.length > 30 && b[0] === 0x52 && b[1] === 0x49 && b[8] === 0x57 && b[9] === 0x45) {
    const chunk = String.fromCharCode(b[12], b[13], b[14], b[15]);
    if (chunk === 'VP8X') return { width: 1 + (b[24] | (b[25] << 8) | (b[26] << 16)), height: 1 + (b[27] | (b[28] << 8) | (b[29] << 16)) };
    if (chunk === 'VP8 ') return { width: (b[26] | (b[27] << 8)) & 0x3fff, height: (b[28] | (b[29] << 8)) & 0x3fff };
    if (chunk === 'VP8L') {
      const bits = b[21] | (b[22] << 8) | (b[23] << 16) | (b[24] << 24);
      return { width: (bits & 0x3fff) + 1, height: ((bits >> 14) & 0x3fff) + 1 };
    }
  }
  return null;
}

/** The image gate. Every cover we store must pass it. */
export async function checkCoverImage(url: string): Promise<CoverCheck> {
  const res = await fetchWithTimeout(url);
  if (!res) return { ok: false, reason: 'unreachable' };
  if (!res.ok) return { ok: false, reason: `http ${res.status}` };
  const type = res.headers.get('content-type') ?? '';
  if (!type.startsWith('image/')) return { ok: false, reason: `not an image (${type})` };
  const bytes = new Uint8Array(await res.arrayBuffer());
  if (bytes.length < 4000) return { ok: false, reason: `too small (${bytes.length} bytes)` };
  const size = imageSize(bytes);
  if (!size) return { ok: false, reason: 'unreadable image' };
  const { width, height } = size;
  if (NO_COVER_DIMENSIONS.some(([w, h]) => Math.abs(width - w) <= 2 && Math.abs(height - h) <= 2)) {
    return { ok: false, reason: `placeholder ${width}x${height}`, width, height };
  }
  const ratio = height / width;
  if (ratio < MIN_RATIO || ratio > MAX_RATIO) {
    return { ok: false, reason: `not book-shaped ${width}x${height}`, width, height };
  }
  if (height < MIN_HEIGHT) return { ok: false, reason: `low-res ${width}x${height}`, width, height };
  return { ok: true, width, height };
}

/**
 * ISBN registration group 0 or 1 = the English-language area (UK, US, Canada,
 * Australia...). 978-85 is Brazil, 978-2 French, 978-3 German, and so on.
 * Under the 979 prefix only 979-8 (USA) is English: 979-10 is France, 979-11
 * Korea, 979-12 Italy. Testing /^97[89][01]/ let a French "Congo" through.
 */
export function isEnglishIsbn(raw: string): boolean {
  const d = (raw ?? '').replace(/[^0-9Xx]/g, '');
  if (d.length === 13) return /^978[01]|^9798/.test(d);
  if (d.length === 10) return /^[01]/.test(d);
  return false;
}

/**
 * Words common in English prose and rare in the other languages the UK Apple
 * store sells (Polish, Italian, French, German, Spanish, Portuguese). Excludes
 * "a", "to", "on", "in", "an", "as": each is an ordinary word elsewhere.
 */
const ENGLISH_MARKERS = new Set([
  'the', 'and', 'of', 'his', 'her', 'their', 'that', 'with', 'for', 'is', 'was',
  'are', 'from', 'this', 'who', 'by', 'he', 'she', 'it', 'its', 'into', 'when',
]);

/** Is this store description English? Needs several marker words in its opening. */
export function looksEnglish(text: string | null | undefined): boolean {
  const words = (text ?? '').replace(/<[^>]*>/g, ' ').toLowerCase()
    .split(/\s+/).map((w) => w.replace(/[^a-z']/g, '')).filter(Boolean).slice(0, 80);
  const hits = words.filter((w) => ENGLISH_MARKERS.has(w)).length;
  return hits >= 4 && hits / Math.max(words.length, 1) >= 0.08;
}

/**
 * Is an Apple store result an English edition? The UK store sells Polish and
 * Italian ebooks under the same title and author: "Eden" by Stanislaw Lem
 * matched a Polish edition (2026-10-10). Apple's artwork filename is often the
 * ISBN; when it is, it decides. Otherwise the description's language does.
 */
export function isEnglishAppleResult(r: { artworkUrl100?: string; description?: string }): boolean {
  const isbn = (r.artworkUrl100 ?? '').match(/\/(97[89]\d{10})[^/]*\/[^/]+$/)?.[1];
  return isbn ? isEnglishIsbn(isbn) : looksEnglish(r.description);
}

/** Lowercase, strip accents and punctuation, collapse spaces. */
export function norm(s: string): string {
  return (s ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l').replace(/Ł/g, 'l')
    .toLowerCase().replace(/&/g, ' and ')
    .replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Row key in public.verified_cover_cache, used by the verified-cover function
 * and by the site to read its answers: case, accents and punctuation ignored.
 */
export function coverCacheKey(title: string, author: string): string {
  return `${norm(title)}|${norm(author)}`;
}

/** Same book title: equal once normalised, ignoring a leading article or a subtitle. */
export function titleMatches(wanted: string, candidate: string): boolean {
  const strip = (t: string) => norm(t.split(/[:(]/)[0]).replace(/^(the|a|an) /, '');
  const a = strip(wanted);
  if (!a) return false;
  if (a === strip(candidate)) return true;
  // A series prefix: stores list "Wolverine: Old Man Logan" and "Star Wars:
  // Heir to the Empire", where the part after the colon IS the book.
  const colon = candidate.indexOf(':');
  return colon > 0 && strip(candidate.slice(colon + 1)) === a;
}

/** "Larry Niven & Jerry Pournelle", "Mark Millar, Steve McNiven" -> one name each. */
function people(names: string): string[][] {
  return names.split(/\s*(?:,|&|;|\band\b)\s*/i)
    .map((n) => norm(n).split(' ').filter((t) => t && !/^(jr|sr|ii|iii|iv)$/.test(t)))
    .filter((p) => p.length > 0);
}

/**
 * Same author: some person in `wanted` matches some person in a candidate by
 * surname and, where both have one, first initial. Co-authors are compared one
 * by one, so "Mark Millar" matches "Mark Millar & Steve McNiven".
 */
export function authorMatches(wanted: string, candidates: string[]): boolean {
  const ws = people(wanted ?? '');
  if (ws.length === 0) return false;
  return candidates.some((c) => people(c ?? '').some((p) => ws.some((w) =>
    (p[p.length - 1] === w[w.length - 1] && (w.length < 2 || p.length < 2 || p[0][0] === w[0][0])) ||
    // Surname-first names: our "Liu Cixin" is Apple's "Cixin Liu".
    (w.length === 2 && p.length === 2 && w[0] === p[1] && w[1] === p[0]))));
}

async function json(url: string): Promise<any | null> {
  const res = await fetchWithTimeout(url);
  if (!res?.ok) return null;
  try { return await res.json(); } catch { return null; }
}

async function tryUrl(url: string, source: VerifiedCover['source']): Promise<VerifiedCover | null> {
  const c = await checkCoverImage(url);
  return c.ok ? { url, source, width: c.width!, height: c.height! } : null;
}

/**
 * Open Library takes many covers from Internet Archive scans, and a scan is
 * often the TITLE PAGE, not the cover: The Pride of Chanur (Mandarin) came
 * back as .../page/title.jpg on 2026-10-10. Only an explicit title-page scan
 * is rejected: archive.org's "_preview", "services/img" and first-leaf images
 * are the item's cover (checked by eye on 24 catalogue covers, 22 were covers).
 */
export function isScannedNonCover(sourceUrl: string | null | undefined): boolean {
  const src = sourceUrl ?? '';
  return /archive\.org/.test(src) && /\/page\/title|[?&]page=title/.test(src);
}

/** Look up an Open Library cover's provenance (its .json twin). */
async function scannedNonCover(metaUrl: string): Promise<boolean> {
  const meta = await json(metaUrl);
  return isScannedNonCover(meta?.source_url);
}

async function fromOpenLibraryIsbn(isbn: string): Promise<VerifiedCover | null> {
  const clean = isbn.replace(/[^0-9Xx]/g, '');
  if (clean.length !== 10 && clean.length !== 13) return null;
  if (await scannedNonCover(`https://covers.openlibrary.org/b/isbn/${clean}.json`)) return null;
  return tryUrl(`https://covers.openlibrary.org/b/isbn/${clean}-L.jpg?default=false`, 'openlibrary-isbn');
}

async function fromOpenLibraryEditions(title: string, author: string): Promise<VerifiedCover | null> {
  const q = `title=${encodeURIComponent(title)}&author=${encodeURIComponent(author)}`;
  const search = await json(`https://openlibrary.org/search.json?${q}&limit=5&fields=key,title,author_name`);
  const work = (search?.docs ?? []).find((d: any) =>
    titleMatches(title, d.title ?? '') && authorMatches(author, d.author_name ?? []));
  if (!work?.key) return null;
  const eds = await json(`https://openlibrary.org${work.key}/editions.json?limit=100`);
  // Open Library's language field is not enough on its own: Neuromancer's
  // Brazilian Aleph edition (ISBN 978-85-...) is tagged English. So an edition
  // must ALSO carry an English-language-area ISBN (see isEnglishIsbn).
  const english = (eds?.entries ?? []).filter((e: any) =>
    (e.languages ?? []).some((l: any) => l?.key === '/languages/eng') &&
    [...(e.isbn_13 ?? []), ...(e.isbn_10 ?? [])].some(isEnglishIsbn) &&
    Array.isArray(e.covers) && e.covers.some((c: number) => c > 0) &&
    // An audiobook's "cover" is the distributor's template, not the book's art
    // (The Pride of Chanur came back as an MP3-CD template, 2026-10-10).
    !/audio|mp3|\bcd\b|cassette/i.test(e.physical_format ?? '') &&
    titleMatches(title, e.title ?? ''));
  for (const e of english.slice(0, 5)) {
    const id = e.covers.find((c: number) => c > 0);
    if (await scannedNonCover(`https://covers.openlibrary.org/b/id/${id}.json`)) continue;
    const hit = await tryUrl(`https://covers.openlibrary.org/b/id/${id}-L.jpg?default=false`, 'openlibrary-edition');
    if (hit) return hit;
  }
  return null;
}

/**
 * An Apple artwork URL at `px` (bounding box; the image keeps its own aspect).
 * Apple has served two formats -- `.../100x100-75.jpg` and `.../100x100bb.jpg`
 * -- and the site's scaler only knew the first, so every Apple cover since the
 * switch was shown at 100px. One helper, used by the cover job, the Apple
 * transformer and the lightbox.
 */
export function appleArtwork(url: string | null | undefined, px: number): string | undefined {
  if (!url) return undefined;
  return url
    .replace(/\/\d+x\d+bb\.(jpg|png|webp)/, `/${px}x${px}bb.$1`)
    .replace(/\/\d+x\d+-75\./, `/${px}x${px}-75.`);
}

/**
 * The same cover at a size fit for where it is SHOWN. Covers are stored at
 * full size (Apple 1200px, Open Library -L) for the enlarge view, and a grid
 * that drew them 64px tall was downloading ~250 KB per book. `px` is the
 * height in device pixels; it is rounded up to a few fixed sizes so the CDNs
 * serve cached copies. Other hosts are returned unchanged.
 */
export function coverAtSize(url: string | null | undefined, px: number): string | undefined {
  if (!url) return undefined;
  if (/mzstatic\.com/.test(url)) {
    const size = [200, 400, 600].find((s) => s >= px) ?? 1200;
    return appleArtwork(url, size);
  }
  // Open Library: M is ~270px tall, L is the original upload.
  if (/covers\.openlibrary\.org\/b\//.test(url) && px <= 270) return url.replace(/-L\.jpg/, '-M.jpg');
  return url;
}

async function fromApple(title: string, author: string): Promise<VerifiedCover | null> {
  const term = encodeURIComponent(`${title} ${author}`);
  const data = await json(`https://itunes.apple.com/search?term=${term}&media=ebook&country=gb&limit=10`);
  for (const r of data?.results ?? []) {
    if (!titleMatches(title, r.trackName ?? '') || !authorMatches(author, [r.artistName ?? ''])) continue;
    if (!isEnglishAppleResult(r)) continue;
    const art: string = r.artworkUrl100 ?? '';
    if (!art) continue;
    const hit = await tryUrl(appleArtwork(art, 1200)!, 'apple');
    if (hit) return hit;
  }
  return null;
}

export async function findVerifiedCover(
  { title, author, isbn }: { title: string; author: string; isbn?: string | null },
): Promise<VerifiedCover | null> {
  if (!title || !author) return null;
  if (isbn && isEnglishIsbn(isbn)) {
    const hit = await fromOpenLibraryIsbn(isbn);
    if (hit) return hit;
  }
  // Apple before Open Library editions: exact title + author on the UK store is
  // the publisher's own artwork at 1200px, sharp in the enlarge view.
  return (await fromApple(title, author)) ?? (await fromOpenLibraryEditions(title, author));
}
